import { createSlice } from '@reduxjs/toolkit'
import { Action } from 'redux'

import actionTypes from 'common/actions/constants/ReduxActionTypes'
import {
  fetchApplicationAction,
  fetchApplicationSource,
  fetchApplicationsTriggers,
} from 'common/actions/integrations/Applications/actions/accountApplicationsActions'
import { SETTINGS_GROUP_FIELD_NAME } from 'common/actions/integrations/Applications/constants'
import {
  IActionNodeApplicationState,
  IActionField,
} from 'common/actions/integrations/Applications/interfaces'

const initialState: IActionNodeApplicationState = {
  selectedApplicationId: null,
  selectedItemOid: null,
  selectedAction: null,
  selectedActionName: null,
  selectedActionSource: null,
  actionFetching: false,
  actionFields: [],
  actionMapping: [],
  actionSources: {},
  triggers: [],
}

interface ActionWithActionData extends Action {
  applicationId: number
  _oid: string
  actionName?: string
  selectedActionSource: IActionNodeApplicationState['selectedActionSource']
}

interface ActionWithFields extends Action {
  fields: IActionField[]
  mapping: IActionField[]
}

export const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchApplicationsTriggers.pending, (state) => {
      state.triggers = []
    })

    builder.addCase(fetchApplicationsTriggers.fulfilled, (state, action) => {
      if (action.payload) {
        state.triggers = action.payload.apps
      }
    })

    builder.addCase(fetchApplicationAction.pending, (state) => {
      state.selectedAction = null
      state.actionFetching = true
    })

    builder.addCase(fetchApplicationAction.fulfilled, (state, action) => {
      if (action.payload) {
        state.selectedAction = action.payload.action
        state.selectedActionName = action.payload.action.name
        state.actionFetching = false
      }
    })

    builder.addCase(fetchApplicationSource.pending, (state, action) => {
      const { applicationId, sourceName } = action.meta.arg ?? {}

      if (applicationId && applicationId !== state.selectedApplicationId) {
        return
      }

      if (sourceName) {
        state.actionSources = {
          ...state.actionSources,
          [sourceName]: { items: [], fetching: true, reset: false },
        }
      }
    })

    builder.addCase(fetchApplicationSource.rejected, (state, action) => {
      const { applicationId, sourceName } = action.meta.arg ?? {}

      if (applicationId && applicationId !== state.selectedApplicationId) {
        return
      }

      if (sourceName) {
        state.actionSources = {
          ...state.actionSources,
          [sourceName]: { ...state.actionSources[sourceName], fetching: false },
        }
      }
    })

    builder.addCase(fetchApplicationSource.fulfilled, (state, action) => {
      const { applicationId, sourceName } = action.meta.arg ?? {}

      if (applicationId && applicationId !== state.selectedApplicationId) {
        return
      }

      if (sourceName && action.payload.source) {
        state.actionSources[sourceName] = { fetching: false, items: action.payload.source }
      }
    })

    builder.addCase(
      actionTypes.APPLICATION_ACTION_SELECT_APPLICATION,
      (state, action: ActionWithActionData) => {
        if ('applicationId' in action && '_oid' in action) {
          state.selectedApplicationId = action.applicationId
          state.selectedItemOid = action._oid
          state.selectedActionName = action.actionName ?? null
          state.selectedActionSource = action.selectedActionSource
        }
      },
    )

    builder.addCase(actionTypes.APPLICATION_ACTION_DESELECT_APPLICATION, (state) => {
      state.selectedAction = null
      state.selectedApplicationId = null
      state.selectedItemOid = null
      state.selectedActionName = null
      state.actionFields = []
      state.actionMapping = []
    })

    builder.addCase(actionTypes.APPLICATION_DESELECT_ACTION, (state) => {
      state.selectedAction = null
      state.selectedActionName = null
    })

    builder.addCase(
      actionTypes.APPLICATION_ACTION_FIELDS_SET,
      (state, action: ActionWithFields) => {
        if ('fields' in action) {
          state.actionFields = action.fields
          state.actionMapping = action.mapping
        }
      },
    )

    builder.addCase(
      actionTypes.APPLICATION_ACTION_FIELD_UPDATE,
      (state, action: ActionWithFields) => {
        if ('fields' in action && action.fields.length) {
          const [field] = action.fields
          const { actionFields } = state
          const index = actionFields.findIndex((el) => el.field_name === field.field_name)
          if (index !== -1) {
            state.actionFields = [
              ...actionFields.slice(0, index),
              { ...actionFields[index], value: field.value },
              ...actionFields.slice(index + 1),
            ]
          }
        }

        if ('mapping' in action && action.mapping.length) {
          const [mapping] = action.mapping
          const { actionMapping } = state
          const index = actionMapping.findIndex((el) => el.field_name === mapping.field_name)
          if (index !== -1) {
            state.actionMapping = [
              ...actionMapping.slice(0, index),
              { ...actionMapping[index], value: mapping.value },
              ...actionMapping.slice(index + 1),
            ]
          }
        }
      },
    )

    builder.addCase(actionTypes.APPLICATION_ACTION_SOURCES_RESET, (state) => {
      const sources = { ...state.actionSources }
      for (const sourceName of Object.keys(sources)) {
        if (sourceName === SETTINGS_GROUP_FIELD_NAME) {
          continue
        }

        sources[sourceName] = { ...sources[sourceName], reset: true }
      }

      const actionFields = state.actionFields.map(
        ({ title, field_name, displayType, value, default: defaultValue, isValid }) => {
          if (displayType === 'select') {
            value = field_name !== SETTINGS_GROUP_FIELD_NAME ? undefined : value
          }
          return {
            title,
            field_name,
            displayType,
            value,
            default: defaultValue,
            isValid,
          }
        },
      )

      state.actionFields = actionFields
      state.actionSources = sources
    })

    builder.addCase(actionTypes.APPLICATION_ACTION_FIELDS_VALIDATE, (state) => {
      state.actionFields = state.actionFields.map((field) => ({
        ...field,
        isValid: field.default !== undefined || field.value !== undefined,
      }))
    })

    builder.addCase(actionTypes.APPLICATION_ACTION_FIELD_VALIDATE, (state, action) => {
      if ('fieldName' in action) {
        const { actionFields } = state
        const index = actionFields.findIndex((el) => el.field_name === action.fieldName)
        if (index !== -1) {
          const field = actionFields[index]
          state.actionFields = [
            ...actionFields.slice(0, index),
            {
              ...actionFields[index],
              isValid: field.default !== undefined || field.value !== undefined,
            },
            ...actionFields.slice(index + 1),
          ]
        }
      }
    })
  },
})

export const { reducer } = applicationsSlice
